import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import TenantMaintenance from '../maintenance/TenantMaintenance';
import StripeAutopaySetup from '../financial/StripeAutopaySetup';

// Add styles for payment sections
const paymentStyles = `
  .payment-method-section {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
  }
  
  .payment-method-section h3 {
    margin: 0 0 15px 0;
    color: #495057;
    font-size: 1.1em;
    font-weight: 600;
  }
  
  .status-indicator {
    display: flex;
    align-items: center;
  }
  
  .status-badge {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.85em;
    font-weight: 500;
  }
  
  .status-badge.active {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }
  
  .status-badge.inactive {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
  
  .toggle-switch {
    position: relative;
    display: inline-block;
    width: 60px;
    height: 34px;
  }
  
  .toggle-switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .toggle-label {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 34px;
  }
  
  .toggle-label:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
  }
  
  input:checked + .toggle-label {
    background-color: #2196F3;
  }
  
  input:checked + .toggle-label:before {
    transform: translateX(26px);
  }
`;

function TenantEditPage() {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const [tenant, setTenant] = useState(null);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [tenantAttributes, setTenantAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddAttributeModal, setShowAddAttributeModal] = useState(false);
  const [newAttribute, setNewAttribute] = useState('');
  const [newAttributeType, setNewAttributeType] = useState('string');
  const [attributeTypes, setAttributeTypes] = useState({});
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [documents, setDocuments] = useState([]);
  const [showDocumentUploadModal, setShowDocumentUploadModal] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  
  // Legal status management
  const [legalStatus, setLegalStatus] = useState({
    status: 'good_standing',
    status_date: null,
    notes: ''
  });
  const [legalStatusHistory, setLegalStatusHistory] = useState([]);
  const [showLegalStatusModal, setShowLegalStatusModal] = useState(false);
  const [newLegalStatus, setNewLegalStatus] = useState({
    status: 'good_standing',
    notes: ''
  });
  
  // Complaint management
  const [complaints, setComplaints] = useState([]);
  const [showAddComplaintModal, setShowAddComplaintModal] = useState(false);
  const [editingComplaint, setEditingComplaint] = useState(null);
  const [newComplaint, setNewComplaint] = useState({
    complaint_type: 'lease_violation',
    severity: 'low',
    description: ''
  });
  const [complaintDocuments, setComplaintDocuments] = useState({});
  const [legalStatusDocuments, setLegalStatusDocuments] = useState({});
  const [showDocumentLinkModal, setShowDocumentLinkModal] = useState(false);
  const [linkingTo, setLinkingTo] = useState(null); // {type: 'complaint'|'legal_status', id: number}
  
  // For linking documents during complaint creation
  const [newComplaintDocuments, setNewComplaintDocuments] = useState([]);
  const [showNewComplaintDocumentModal, setShowNewComplaintDocumentModal] = useState(false);
  
  // Upcoming payments state
  const [upcomingPayments, setUpcomingPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  
  // Payment processing state
  const [showPaymentProcessing, setShowPaymentProcessing] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [showStripeAutopayModal, setShowStripeAutopayModal] = useState(false);
  const [checkFile, setCheckFile] = useState(null);
  const [checkUploading, setCheckUploading] = useState(false);
  const [creditCardData, setCreditCardData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
    billingAddress: ''
  });
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');


  useEffect(() => {
    const storedUser = localStorage.getItem('username');
    if (!storedUser) {
      navigate('/');
      return;
    }
    
    fetchTenantData();
    fetchProperties();
    fetchAttributeTypes();
    fetchDocuments();
    fetchLegalStatus();
    fetchComplaints();
    fetchUpcomingPayments();
  }, [tenantId, navigate]);

  const fetchTenantData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenants`);
      if (!response.ok) throw new Error('Failed to fetch tenants');
      
      const tenants = await response.json();
      const currentTenant = tenants.find(t => t.id === parseInt(tenantId));
      
      if (!currentTenant) {
        setError('Tenant not found');
        return;
      }
      
      setTenant(currentTenant);
      
      // Fetch units if property exists
      if (currentTenant.property_id) {
        fetchUnits(currentTenant.property_id);
      }
      
      // Fetch attributes
      const attrResponse = await fetch(`${API_BASE_URL}/api/tenants/attributes`);
      if (attrResponse.ok) {
        const attrData = await attrResponse.json();
        setTenantAttributes(attrData.attributes || attrData);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties`);
      if (response.ok) {
        const data = await response.json();
        setProperties(data);
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
    }
  };

  const fetchUnits = async (propertyId) => {
    if (!propertyId) {
      setUnits([]);
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/units`);
      if (response.ok) {
        const data = await response.json();
        setUnits(data);
      }
    } catch (err) {
      console.error('Error fetching units:', err);
    }
  };

  const handlePropertyChange = (propertyId) => {
    const property = properties.find(p => p.id === parseInt(propertyId));
    const updates = { 
      property_id: propertyId ? parseInt(propertyId) : null,
      unit_id: null, // Reset unit when property changes
      'Unit Address': '' // Reset unit address
    };

    if (property) {
      // Auto-populate address fields from property
      const propertyAddress = property.address || property['Address'] || '';
      const propertyCity = property.city || property['City'] || '';
      const propertyState = property.state || property['State'] || '';
      const propertyZip = property.zip || property['Zip'] || property['Zip Code'] || property.zip_code || '';
      
      // Parse address if it contains city/state/zip
      if (propertyAddress && !propertyCity && !propertyState) {
        // Try to parse address that might contain city, state, zip
        const addressParts = propertyAddress.split(',').map(p => p.trim());
        if (addressParts.length >= 2) {
          updates['Address'] = addressParts[0];
          const cityStateZip = addressParts[1].split(/\s+/);
          if (cityStateZip.length >= 2) {
            updates['City'] = cityStateZip[0];
            updates['State'] = cityStateZip[1];
            if (cityStateZip.length >= 3) {
              updates['Zip'] = cityStateZip.slice(2).join(' ');
            }
          }
        } else {
          updates['Address'] = propertyAddress;
        }
      } else {
        updates['Address'] = propertyAddress;
      }
      
      // Set city, state, zip if available
      if (propertyCity) updates['City'] = propertyCity;
      if (propertyState) updates['State'] = propertyState;
      if (propertyZip) {
        updates['Zip'] = propertyZip;
        updates['Zip Code'] = propertyZip;
      }
    } else {
      // Clear address fields when property is deselected
      updates['Address'] = '';
      updates['City'] = '';
      updates['State'] = '';
      updates['Zip'] = '';
      updates['Zip Code'] = '';
    }

    setTenant({ ...tenant, ...updates });
    if (propertyId) {
      fetchUnits(propertyId);
    } else {
      setUnits([]);
    }
  };

  const handleUnitChange = (unitId) => {
    const unit = units.find(u => u.id === parseInt(unitId));
    const property = properties.find(p => p.id === parseInt(tenant.property_id));
    
    if (unit && property) {
      // Get property address fields
      const propertyAddress = property.address || property['Address'] || '';
      const propertyCity = property.city || property['City'] || '';
      const propertyState = property.state || property['State'] || '';
      const propertyZip = property.zip || property['Zip'] || property['Zip Code'] || property.zip_code || '';
      
      // Build unit address: insert unit number after street address
      let unitAddress = '';
      if (propertyAddress) {
        // If address has comma (contains city/state/zip), insert unit after street part
        if (propertyAddress.includes(',')) {
          const addressParts = propertyAddress.split(',').map(p => p.trim());
          // Insert unit number after the first part (street address)
          addressParts.splice(1, 0, `Unit ${unit.unit_number}`);
          unitAddress = addressParts.join(', ');
        } else {
          // Just street address, append unit
          unitAddress = `${propertyAddress}, Unit ${unit.unit_number}`;
          // Add city, state, zip if they exist separately
          if (propertyCity) unitAddress += `, ${propertyCity}`;
          if (propertyState) unitAddress += `, ${propertyState}`;
          if (propertyZip) unitAddress += `, ${propertyZip}`;
        }
      } else {
        // Fallback if no property address
        unitAddress = `Unit ${unit.unit_number}`;
        if (propertyCity) unitAddress += `, ${propertyCity}`;
        if (propertyState) unitAddress += `, ${propertyState}`;
        if (propertyZip) unitAddress += `, ${propertyZip}`;
      }
      
      setTenant({
        ...tenant,
        unit_id: parseInt(unitId),
        'Unit Address': unitAddress
      });
    } else {
      setTenant({
        ...tenant,
        unit_id: null,
        'Unit Address': ''
      });
    }
  };

  const fetchAttributeTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenants/attribute-types`);
      if (response.ok) {
        const data = await response.json();
        setAttributeTypes(data.attribute_types || data);
      }
    } catch (err) {
      console.error('Error fetching attribute types:', err);
    }
  };

  const handleDeactivateAutopay = async () => {
    if (!window.confirm('Are you sure you want to deactivate autopay for this tenant?')) {
      return;
    }
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenant/${tenant.id}/autopay`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to deactivate autopay');
      
      // Update local state to reflect the change
      setTenant({
        ...tenant,
        'Autopay Is Active': 0
      });
      
      setSuccess('Autopay deactivated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateCreditCard = async () => {
    if (!window.confirm('Are you sure you want to deactivate credit card for this tenant?')) {
      return;
    }
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenant/${tenant.id}/credit-card`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to deactivate credit card');
      
      // Update local state to reflect the change
      setTenant({
        ...tenant,
        'Credit Card Is Active': 0
      });
      
      setSuccess('Credit card deactivated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!tenant) {
      setError('No tenant data to save');
      return;
    }
    
    console.log('🔄 Starting save process...');
    console.log('📋 Current tenant data:', tenant);
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      // 🔧 CRITICAL FIX: Ensure ALL expected fields are included with proper values
      const transformedTenant = {
        // Always include the ID
        id: tenant.id,
        
        // Basic information fields
        'First Name': tenant['First Name'] || tenant['first_name'] || '',
        'Last Name': tenant['Last Name'] || tenant['last_name'] || '',
        'Email': tenant['Email'] || tenant['email'] || '',
        'Phone': tenant['Phone'] || tenant['phone'] || '',
        'Social Security': tenant['Social Security'] || tenant['social_security'] || null,
        'Unit Address': tenant['Unit Address'] || tenant['unit_address'] || '',
        
        // Address fields - auto-populated from property
        'Address': tenant['Address'] || tenant['address'] || '',
        'City': tenant['City'] || tenant['city'] || '',
        'State': tenant['State'] || tenant['state'] || '',
        'Zip': tenant['Zip'] || tenant['zip'] || tenant['Zip Code'] || tenant['zip_code'] || '',
        'Zip Code': tenant['Zip Code'] || tenant['zip_code'] || tenant['Zip'] || tenant['zip'] || '',
        
        // Unit ID - needed for linking tenant to unit
        'Unit Id': tenant.unit_id || tenant['Unit Id'] || null,
        
        // Property and tenant code
        'Property Id': (() => {
          let propertyId = tenant['property_id'] || tenant['Property Id'] || tenant['Property ID'];
          if (propertyId === '' || propertyId === 0 || propertyId === '0' || propertyId === undefined) {
            return null;
          } else if (typeof propertyId === 'string') {
            const parsed = parseInt(propertyId);
            return isNaN(parsed) ? null : parsed;
          }
          return propertyId;
        })(),
        'Tenant Code': tenant['Tenant Code'] || tenant['tenant_code'] || '',
        
        // Status fields
        'Resident Status': tenant['Resident Status'] || tenant['resident_status'] || '',
        'Lease Status': tenant['Lease Status'] || tenant['lease_status'] || '',
        
        // Date fields
        'Move-In Date': tenant['Move-In Date'] || tenant['move_in_date'] || '',
        'Move-Out Date': tenant['Move-Out Date'] || tenant['move_out_date'] || '',
        
        // Additional personal fields
        'Gender': tenant['Gender'] || tenant['gender'] || '',
        
        // Financial fields
        'Monthly Rent': tenant['Monthly Rent'] || tenant['monthly_rent'] || '',
        'Credit Score': tenant['Credit Score'] || tenant['credit_score'] || '',
        'Rent Amount': tenant['Rent Amount'] || tenant['rent_amount'] || '',
        'Security Deposit': tenant['Security Deposit'] || tenant['security_deposit'] || '',
        
        // Employment fields
        'Employment Status': tenant['Employment Status'] || tenant['employment_status'] || '',
        'Employer Name': tenant['Employer Name'] || tenant['employer_name'] || '',
        'Monthly Income': tenant['Monthly Income'] || tenant['monthly_income'] || '',
        
        // Insurance and payment fields
        'Insurance Provider': tenant['Insurance Provider'] || tenant['insurance_provider'] || '',
        'Payment Method': tenant['Payment Method'] || tenant['payment_method'] || '',
        
        // Emergency contact fields
        'Emergency Contact Name': tenant['Emergency Contact Name'] || tenant['emergency_contact_name'] || '',
        'Emergency Contact Phone': tenant['Emergency Contact Phone'] || tenant['emergency_contact_phone'] || '',
        'Emergency Contact Relationship': tenant['Emergency Contact Relationship'] || tenant['emergency_contact_relationship'] || '',
        
        // Autopay fields
        'Autopay Bank Name': tenant['Autopay Bank Name'] || tenant['autopay_bank_name'] || '',
        'Autopay Account Type': tenant['Autopay Account Type'] || tenant['autopay_account_type'] || '',
        'Autopay Account Number': tenant['Autopay Account Number'] || tenant['autopay_account_number'] || '',
        'Autopay Routing Number': tenant['Autopay Routing Number'] || tenant['autopay_routing_number'] || '',
        'Autopay Account Holder Name': tenant['Autopay Account Holder Name'] || tenant['autopay_account_holder_name'] || '',
        'Autopay Is Active': tenant['Autopay Is Active'] || tenant['autopay_is_active'] || 0,
        
        // Credit card fields
        'Credit Card Number': tenant['Credit Card Number'] || tenant['credit_card_number'] || '',
        'Credit Card Expiry': tenant['Credit Card Expiry'] || tenant['credit_card_expiry'] || '',
        'Credit Card CVV': tenant['Credit Card CVV'] || tenant['credit_card_cvv'] || '',
        'Credit Card Holder Name': tenant['Credit Card Holder Name'] || tenant['credit_card_holder_name'] || '',
        'Credit Card Is Active': tenant['Credit Card Is Active'] || tenant['credit_card_is_active'] || 0,
        
        // Other fields
        'Autopay Enabled': tenant['Autopay Enabled'] || tenant['autopay_enabled'] || 0,
        'Autopay Method': tenant['Autopay Method'] || tenant['autopay_method'] || '',
        'Profile Image': tenant['Profile Image'] || tenant['profile_image'] || '',
        'Lease Start Date': tenant['Lease Start Date'] || tenant['lease_start_date'] || '',
        'Lease End Date': tenant['Lease End Date'] || tenant['lease_end_date'] || '',
        'Lease Type': tenant['Lease Type'] || tenant['lease_type'] || ''
      };
      
      // Add any additional custom attributes that might exist
      Object.keys(tenant).forEach(key => {
        if (!transformedTenant.hasOwnProperty(key) && 
            !transformedTenant.hasOwnProperty(key.split('_').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' '))) {
          // This is a custom attribute, include it
          const titleCaseKey = key.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ');
          transformedTenant[titleCaseKey] = tenant[key];
        }
      });
      
      console.log('🔄 Transformed data for backend:', transformedTenant);
      console.log('🌐 Making API call to:', `${API_BASE_URL}/api/tenants/${tenant.id}`);
      
      const response = await fetch(`${API_BASE_URL}/api/tenants/${tenant.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(transformedTenant)
      });
      
      console.log('📡 API Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ API Error:', errorData);
        throw new Error(errorData.error || `Failed to update tenant: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('✅ API Success:', result);
      
      // If autopay information is being updated, also save to autopay endpoint
      const autopayMethod = tenant['Autopay Method'];
      if (tenant['Autopay Enabled'] && autopayMethod) {
        try {
          let autopayData;
          
          if (autopayMethod === 'credit' || autopayMethod === 'card') {
            // Credit card autopay
            if (tenant['Credit Card Number'] && tenant['Credit Card Expiry'] && tenant['Credit Card CVV']) {
              autopayData = {
                autopay_method: 'credit',
                card_number: tenant['Credit Card Number'].replace(/\s/g, '').replace(/-/g, ''),
                card_expiry: tenant['Credit Card Expiry'],
                card_cvv: tenant['Credit Card CVV'],
                cardholder_name: tenant['Credit Card Holder Name'] || ''
              };
            }
          } else if (autopayMethod === 'bank') {
            // Bank account autopay
            if (tenant['Autopay Bank Name'] && tenant['Autopay Account Number'] && tenant['Autopay Routing Number']) {
              autopayData = {
                autopay_method: 'bank',
                bank_name: tenant['Autopay Bank Name'],
                account_type: tenant['Autopay Account Type'] || 'checking',
                account_number: tenant['Autopay Account Number'],
                routing_number: tenant['Autopay Routing Number'],
                account_holder_name: tenant['Autopay Account Holder Name'] || ''
              };
            }
          }
          
          if (autopayData) {
            console.log('💳 Saving autopay info:', autopayData);
            const autopayResponse = await fetch(`${API_BASE_URL}/api/tenant/${tenant.id}/autopay`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(autopayData)
            });
            
            if (autopayResponse.ok) {
              console.log('✅ Autopay information also saved successfully');
            }
          }
        } catch (autopayErr) {
          console.warn('⚠️ Failed to save autopay info:', autopayErr);
          // Don't fail the main save operation for this
        }
      }
      
      setSuccess('✅ Tenant updated successfully!');
      console.log('🎉 Save completed successfully!');
      
      // If unit_id is set, assign tenant to unit (this will update unit status to occupied)
      const unitId = tenant.unit_id;
      if (unitId) {
        try {
          console.log(`🔗 Assigning tenant ${tenant.id} to unit ${unitId}...`);
          const unitAssignResponse = await fetch(`${API_BASE_URL}/api/units/${unitId}/assign-tenant`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tenant_id: tenant.id,
              is_primary: true
            })
          });
          
          if (unitAssignResponse.ok) {
            console.log('✅ Tenant assigned to unit successfully - unit status updated to occupied');
          } else {
            const errorData = await unitAssignResponse.json().catch(() => ({}));
            console.warn('⚠️ Failed to assign tenant to unit:', errorData);
          }
        } catch (unitErr) {
          console.warn('⚠️ Error assigning tenant to unit:', unitErr);
          // Don't fail the main save operation for this
        }
      }
      
      // Refresh the tenant data to show updated information
      await fetchTenantData();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('❌ Save failed:', err);
      setError(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
      console.log('🔄 Save process completed');
    }
  };

  const handleAddAttribute = async () => {
    if (!newAttribute.trim()) return;
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      setSuccess(`Adding attribute "${newAttribute}" and propagating to all tenants...`);
      
      const response = await fetch(`${API_BASE_URL}/api/tenants/attributes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          attribute: newAttribute,
          type: newAttributeType
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to add attribute');
      }
      
      const result = await response.json();
      
      // Refresh data
      setSuccess(`Attribute "${newAttribute}" added successfully! Refreshing data...`);
      await fetchTenantData();
      await fetchAttributeTypes();
      
      setShowAddAttributeModal(false);
      setNewAttribute('');
      setNewAttributeType('string');
      setSuccess(`✅ Attribute "${newAttribute}" added successfully and propagated to all tenants!`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(`Failed to add attribute: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSyncAttributes = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      setSuccess('Synchronizing tenant attributes across all tenants...');
      
      const response = await fetch(`${API_BASE_URL}/api/tenants/sync-attributes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to sync attributes');
      }
      
      const result = await response.json();
      
      // Refresh data
      setSuccess('Attributes synchronized successfully! Refreshing data...');
      await fetchTenantData();
      await fetchAttributeTypes();
      
      setSuccess('✅ All tenant attributes have been synchronized successfully!');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(`Failed to sync attributes: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleMigrateDatabase = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      setSuccess('Migrating tenant database to new schema...');
      
      const response = await fetch(`${API_BASE_URL}/api/tenants/migrate-database`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to migrate database');
      }
      
      const result = await response.json();
      
      // Refresh data after migration
      setSuccess('Database migrated successfully! Refreshing data...');
      await fetchTenantData();
      await fetchAttributeTypes();
      
      setSuccess('✅ Tenant database has been migrated to the new schema successfully!');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(`Failed to migrate database: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this tenant? This action cannot be undone.')) {
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenants/${tenant.id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to delete tenant');
      
      navigate('/databases');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const getPropertyName = (propertyId) => {
    if (!propertyId) return 'No Property Assigned';
    const property = properties.find(p => p.id === propertyId);
    return property ? property['Property Name'] || property.property_name || `Property ${propertyId}` : 'Unknown Property';
  };

  const formatCurrency = (value) => {
    if (isNaN(Number(value))) return value;
    const n = Number(value);
    const formatted = n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    return n < 0 ? `(${formatted.replace('-', '')})` : formatted;
  };

  const calculateLeaseEndDate = (startDate, leaseType) => {
    if (!startDate || !leaseType) return '';
    
    const start = new Date(startDate);
    if (isNaN(start.getTime())) return '';
    
    const months = parseInt(leaseType);
    if (isNaN(months)) return '';
    
    const end = new Date(start);
    end.setMonth(end.getMonth() + months);
    end.setDate(end.getDate() - 1); // End date is one day before the next month
    
    return end.toISOString().split('T')[0];
  };

  const handleProfileImageChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check if file is an image
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      // Show preview immediately
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImageUrl(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Upload to server
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        console.log('Uploading profile image for tenant:', tenantId);
        const response = await fetch(`${API_BASE_URL}/api/tenants/${tenantId}/profile-image`, {
          method: 'POST',
          body: formData
        });
        
        console.log('Upload response status:', response.status);
        
        if (!response.ok) {
          const errorData = await response.json();
          console.error('Upload error:', errorData);
          throw new Error(errorData.error || 'Failed to upload profile image');
        }
        
        const result = await response.json();
        console.log('Upload success:', result);
        
        setSuccess('Profile image updated successfully!');
        setTimeout(() => setSuccess(''), 3000);
        
        // Refresh tenant data to get the updated profile_image field
        await fetchTenantData();
      } catch (err) {
        console.error('Profile image upload error:', err);
        setError(err.message);
        // Revert preview on error
        setProfileImageUrl('');
      }
    }
  };

  const getProfileDisplay = () => {
    console.log('Profile display - profileImageUrl:', profileImageUrl);
    console.log('Profile display - tenant profile_image:', tenant?.profile_image);
    
    if (profileImageUrl) {
      return <img src={profileImageUrl} alt="Profile" className="profile-image" />;
    }
    if (tenant?.profile_image) {
      const imageUrl = `${API_BASE_URL}/api/tenants/${tenantId}/profile-image`;
      console.log('Loading profile image from:', imageUrl);
      return <img src={imageUrl} alt="Profile" className="profile-image" />;
    }
    return (
      <div className="profile-initial">
        {tenant['First Name']?.charAt(0) || 'T'}
      </div>
    );
  };

  // Tenant Notes state and handlers
  const [tenantNotes, setTenantNotes] = useState([]);
  const [tenantNewNote, setTenantNewNote] = useState('');

  useEffect(() => {
    // fetch notes when tenantId changes and after tenant data loads
    const fetchNotes = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/tenants/${tenantId}/notes`);
        if (res.ok) {
          const data = await res.json();
          setTenantNotes(data.notes || []);
        }
      } catch (_) {}
    };
    fetchNotes();
  }, [tenantId]);

  const handleSyncPayments = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_BASE_URL}/api/tenant/${tenantId}/generate-payments`, {
        method: 'POST'
      });
      if (response.ok) {
        setSuccess('✅ Rental payments synchronized successfully based on lease terms!');
        fetchUpcomingPayments();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to sync payments');
      }
    } catch (err) {
      console.error('Error syncing payments:', err);
      setError('Error syncing payments');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTenantNote = async () => {
    const text = (tenantNewNote || '').trim();
    if (!text) return;
    try {
      const createdBy = localStorage.getItem('username') || '';
      const res = await fetch(`${API_BASE_URL}/api/tenants/${tenantId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note_text: text, created_by: createdBy })
      });
      if (res.ok) {
        const data = await res.json();
        setTenantNotes(prev => [data.note, ...prev]);
        setTenantNewNote('');
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenants/${tenantId}/documents`);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  };

  const fetchLegalStatus = async () => {
    if (!tenantId) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenants/${tenantId}/legal-status`);
      if (response.ok) {
        const data = await response.json();
        setLegalStatus(data);
      }
    } catch (err) {
      console.error('Error fetching legal status:', err);
    }
  };

  const fetchLegalStatusHistory = async () => {
    if (!tenantId) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenants/${tenantId}/legal-status/history`);
      if (response.ok) {
        const data = await response.json();
        setLegalStatusHistory(data);
      }
    } catch (err) {
      console.error('Error fetching legal status history:', err);
    }
  };

  const fetchComplaints = async () => {
    if (!tenantId) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenants/${tenantId}/complaints`);
      if (response.ok) {
        const data = await response.json();
        setComplaints(data);
        
        // Fetch documents for each complaint
        const documentsData = {};
        for (const complaint of data) {
          try {
            const docResponse = await fetch(`${API_BASE_URL}/api/complaints/${complaint.id}/documents`);
            if (docResponse.ok) {
              const docData = await docResponse.json();
              documentsData[complaint.id] = docData;
            }
          } catch (err) {
            console.error(`Error fetching documents for complaint ${complaint.id}:`, err);
          }
        }
        setComplaintDocuments(documentsData);
      }
    } catch (err) {
      console.error('Error fetching complaints:', err);
    }
  };

  const fetchUpcomingPayments = async () => {
    if (!tenantId) return;
    
    try {
      setLoadingPayments(true);
      const response = await fetch(`${API_BASE_URL}/api/tenants/${tenantId}/upcoming-payments?months=12`);
      if (response.ok) {
        const data = await response.json();
        setUpcomingPayments(data);
      }
    } catch (err) {
      console.error('Error fetching upcoming payments:', err);
    } finally {
      setLoadingPayments(false);
    }
  };

  // Payment processing functions
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCheckFile(file);
    }
  };

  const handleProcessPayment = async (paymentId, paymentMethod) => {
    if (!paymentMethod) {
      setError('Please select a payment method');
      return;
    }

    try {
      setProcessingPayment(true);

      // If it's a check payment and we have a file, upload it first
      if (paymentMethod === 'Check' && checkFile) {
        setCheckUploading(true);
        
        const formData = new FormData();
        formData.append('file', checkFile);
        formData.append('description', `Check payment for tenant ${tenantId}`);
        
        const uploadResponse = await fetch(`${API_BASE_URL}/api/tenants/${tenantId}/documents`, {
          method: 'POST',
          body: formData
        });
        
        if (!uploadResponse.ok) {
          throw new Error('Failed to upload check document');
        }
        
        setCheckUploading(false);
      }

      // Process the payment
      const paymentData = {
        tenant_id: parseInt(tenantId),
        property_id: tenant?.property_id,
        amount: processingPayment.amount,
        payment_method: paymentMethod
      };

      const response = await fetch(`${API_BASE_URL}/api/payments/mock-submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentData)
      });

      if (!response.ok) {
        throw new Error('Failed to process payment');
      }

      const result = await response.json();
      
      // If payment requires confirmation (Stripe Payment Intent)
      if (result.requires_confirmation) {
        setError('Payment intent created. Please complete payment using Stripe.');
        return;
      }
      
      // Update payment status to paid
      const updateResponse = await fetch(`${API_BASE_URL}/api/financial/payments/${paymentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'paid',
          payment_method: paymentMethod,
          reference_number: result.transaction_id || result.payment_intent_id,
          paid_date: new Date().toISOString().split('T')[0]
        })
      });

      if (updateResponse.ok) {
        setSuccess('Payment processed successfully!');
        setShowPaymentProcessing(false);
        setProcessingPayment(null);
        setSelectedPaymentMethod('');
        setCheckFile(null);
        setCreditCardData({
          cardNumber: '',
          expiryDate: '',
          cvv: '',
          cardholderName: '',
          billingAddress: ''
        });
        
        // Refresh payments
        await fetchUpcomingPayments();
      } else {
        throw new Error('Failed to update payment status');
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setProcessingPayment(false);
      setCheckUploading(false);
    }
  };

  const fetchLegalStatusDocuments = async (legalStatusId) => {
    if (!legalStatusId) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/legal-status/${legalStatusId}/documents`);
      if (response.ok) {
        const data = await response.json();
        setLegalStatusDocuments(prev => ({
          ...prev,
          [legalStatusId]: data
        }));
      }
    } catch (err) {
      console.error('Error fetching legal status documents:', err);
    }
  };

  const fetchComplaintDocuments = async (complaintId) => {
    if (!complaintId) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/complaints/${complaintId}/documents`);
      if (response.ok) {
        const data = await response.json();
        setComplaintDocuments(prev => ({
          ...prev,
          [complaintId]: data
        }));
      }
    } catch (err) {
      console.error('Error fetching complaint documents:', err);
    }
  };

  const handleDocumentUpload = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    
    setUploadingDocument(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenants/${tenantId}/documents`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) throw new Error('Failed to upload document');
      
      await fetchDocuments();
      setShowDocumentUploadModal(false);
      event.target.reset();
      setSuccess('Document uploaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDocumentDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/documents/${documentId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to delete document');
      
      await fetchDocuments();
      setSuccess('Document deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDocumentDownload = async (documentId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/documents/${documentId}/download`);
      if (!response.ok) throw new Error('Failed to download document');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err.message);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Legal Status Handlers
  const handleUpdateLegalStatus = async () => {
    if (!newLegalStatus.status) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenants/${tenantId}/legal-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newLegalStatus)
      });
      
      if (!response.ok) throw new Error('Failed to update legal status');
      
      const result = await response.json();
      await fetchLegalStatus();
      await fetchLegalStatusHistory();
      
      // If we have a legal status ID, fetch its documents
      if (result.id) {
        await fetchLegalStatusDocuments(result.id);
      }
      
      setShowLegalStatusModal(false);
      setNewLegalStatus({ status: 'good_standing', notes: '' });
      setSuccess('Legal status updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openLegalStatusModal = () => {
    setNewLegalStatus({ status: legalStatus.status, notes: '' });
    setShowLegalStatusModal(true);
  };

  // Complaint Handlers
  const handleAddComplaint = async () => {
    if (!newComplaint.description.trim()) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenants/${tenantId}/complaints`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newComplaint)
      });
      
      if (!response.ok) throw new Error('Failed to create complaint');
      
      const result = await response.json();
      
      // Link documents if any were selected
      if (newComplaintDocuments.length > 0 && result.id) {
        for (const document of newComplaintDocuments) {
          await fetch(`${API_BASE_URL}/api/complaints/${result.id}/documents`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ document_id: document.id })
          });
        }
      }
      
      await fetchComplaints();
      
      // If we have a complaint ID, fetch its documents
      if (result.id) {
        await fetchComplaintDocuments(result.id);
      }
      
      // Handle automatic legal status change
      if (result.legal_status_updated) {
        await fetchLegalStatus();
        await fetchLegalStatusHistory();
        setSuccess(`Complaint created successfully! Legal status automatically updated to ${getLegalStatusLabel(result.new_legal_status)} (${result.total_complaints} total complaints)`);
      } else {
        setSuccess('Complaint created successfully!');
      }
      setTimeout(() => setSuccess(''), 5000);
      
      setShowAddComplaintModal(false);
      setNewComplaint({ complaint_type: 'lease_violation', severity: 'low', description: '' });
      setNewComplaintDocuments([]); // Reset linked documents
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateComplaint = async () => {
    if (!editingComplaint || !editingComplaint.description.trim()) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/complaints/${editingComplaint.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingComplaint)
      });
      
      if (!response.ok) throw new Error('Failed to update complaint');
      
      await fetchComplaints();
      setEditingComplaint(null);
      setSuccess('Complaint updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteComplaint = async (complaintId) => {
    if (!window.confirm('Are you sure you want to delete this complaint?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/complaints/${complaintId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to delete complaint');
      
      await fetchComplaints();
      setSuccess('Complaint deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const openEditComplaintModal = (complaint) => {
    setEditingComplaint({ ...complaint });
  };

  // Document linking functions
  const openDocumentLinkModal = (type, id) => {
    setLinkingTo({ type, id });
    setShowDocumentLinkModal(true);
  };

  const openNewComplaintDocumentModal = () => {
    setShowNewComplaintDocumentModal(true);
  };

  const linkDocumentToNewComplaint = (documentId) => {
    const document = documents.find(doc => doc.id === documentId);
    if (document && !newComplaintDocuments.find(doc => doc.id === documentId)) {
      setNewComplaintDocuments([...newComplaintDocuments, document]);
    }
    setShowNewComplaintDocumentModal(false);
  };

  const unlinkDocumentFromNewComplaint = (documentId) => {
    setNewComplaintDocuments(newComplaintDocuments.filter(doc => doc.id !== documentId));
  };

  const linkDocument = async (documentId) => {
    if (!linkingTo) return;
    
    try {
      const endpoint = linkingTo.type === 'complaint' 
        ? `${API_BASE_URL}/api/complaints/${linkingTo.id}/documents`
        : `${API_BASE_URL}/api/legal-status/${linkingTo.id}/documents`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_id: documentId })
      });
      
      if (!response.ok) throw new Error('Failed to link document');
      
      // Refresh the appropriate data
      if (linkingTo.type === 'complaint') {
        await fetchComplaints();
      } else {
        await fetchLegalStatusDocuments(linkingTo.id);
      }
      
      setShowDocumentLinkModal(false);
      setLinkingTo(null);
      setSuccess('Document linked successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const unlinkDocument = async (documentId, type, id) => {
    try {
      const endpoint = type === 'complaint' 
        ? `${API_BASE_URL}/api/complaints/${id}/documents/${documentId}`
        : `${API_BASE_URL}/api/legal-status/${id}/documents/${documentId}`;
      
      const response = await fetch(endpoint, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to unlink document');
      
      // Refresh the appropriate data
      if (type === 'complaint') {
        await fetchComplaints();
      } else {
        await fetchLegalStatusDocuments(id);
      }
      
      setSuccess('Document unlinked successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  // Helper functions for display
  const getLegalStatusColor = (status) => {
    switch (status) {
      case 'good_standing': return 'green';
      case 'light_offender': return 'orange';
      case 'heavy_offender': return 'red';
      case 'on_notice': return 'yellow';
      case 'eviction': return 'darkred';
      default: return 'gray';
    }
  };

  const getLegalStatusLabel = (status) => {
    switch (status) {
      case 'good_standing': return 'Good Standing';
      case 'light_offender': return 'Light Offender';
      case 'heavy_offender': return 'Heavy Offender';
      case 'on_notice': return 'On Notice';
      case 'eviction': return 'Eviction';
      default: return status;
    }
  };

  const getComplaintTypeLabel = (type) => {
    switch (type) {
      case 'lease_violation': return 'Lease Violation';
      case 'rent_late': return 'Rent Late';
      case 'utility_late': return 'Utility Late';
      case 'noise_complaint': return 'Noise Complaint';
      case 'property_damage': return 'Property Damage';
      case 'unauthorized_pet': return 'Unauthorized Pet';
      case 'unauthorized_occupant': return 'Unauthorized Occupant';
      case 'other': return 'Other';
      default: return type;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'green';
      case 'medium': return 'orange';
      case 'high': return 'red';
      case 'critical': return 'darkred';
      default: return 'gray';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'red';
      case 'investigating': return 'orange';
      case 'resolved': return 'green';
      case 'dismissed': return 'gray';
      default: return 'gray';
    }
  };

  if (loading) {
    return (
      <div className="tenant-edit-page" style={{ background: '#fff', minHeight: '100vh' }}>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="tenant-edit-page" style={{ background: '#fff', minHeight: '100vh' }}>
        <div className="error-message">Tenant not found</div>
      </div>
    );
  }

  return (
    <div className="tenant-edit-page" style={{ background: '#fff', minHeight: '100vh' }}>
      <style>{paymentStyles}</style>
      {/* Header - sticky and solid white background */}
      <div className="tenant-header" style={{ position: 'sticky', top: 0, zIndex: 10, background: '#ffffff', padding: '16px 20px', borderBottom: '1px solid #e9ecef', marginBottom: '20px' }}>
        <div className="tenant-profile">
          <div className="profile-picture">
            {getProfileDisplay()}
            <input
              type="file"
              accept="image/*"
              onChange={handleProfileImageChange}
              className="profile-image-input"
              id="profile-image-input"
            />
            <label htmlFor="profile-image-input" className="profile-image-label">
              📷
            </label>
          </div>
          <div className="tenant-info">
            <h1>{tenant['First Name']} {tenant['Last Name']}</h1>
            <p className="tenant-subtitle">Tenant Profile</p>
            <p className="tenant-property">📍 {getPropertyName(tenant.property_id)}</p>
          </div>
        </div>
        <div className="header-actions">
          <button 
            onClick={() => navigate('/databases')} 
            className="back-button"
          >
            ← Back to Database
          </button>
          <button 
            onClick={(e) => {
              console.log('💾 Save button clicked!');
              e.preventDefault();
              handleSave();
            }}
            className="save-button"
            disabled={saving}
            type="button"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button 
            onClick={handleDelete} 
            className="delete-button"
            disabled={saving}
          >
            Delete Tenant
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && <div className="error-message" style={{background: '#f8d7da', color: '#721c24', padding: '10px', margin: '10px 0', borderRadius: '4px', border: '1px solid #f5c6cb'}}>{error}</div>}
      {success && <div className="success-message" style={{background: '#d4edda', color: '#155724', padding: '10px', margin: '10px 0', borderRadius: '4px', border: '1px solid #c3e6cb'}}>{success}</div>}
      {loading && <div style={{background: '#d1ecf1', color: '#0c5460', padding: '10px', margin: '10px 0', borderRadius: '4px', border: '1px solid #bee5eb'}}>Loading tenant data...</div>}
      {saving && <div style={{background: '#fff3cd', color: '#856404', padding: '10px', margin: '10px 0', borderRadius: '4px', border: '1px solid #ffeaa7'}}>Saving changes...</div>}

             {/* Main Content */}
       <div className="tenant-content">
         <div className="content-grid">
           {/* Basic Information */}
           <div className="info-section">
             <h2>Basic Information</h2>
             <div className="form-grid">
               <div className="form-field">
                 <label>First Name *</label>
                 <input
                   type="text"
                   value={tenant['First Name'] || ''}
                   onChange={(e) => setTenant({...tenant, 'First Name': e.target.value})}
                   className="form-input"
                 />
               </div>
               <div className="form-field">
                 <label>Last Name *</label>
                 <input
                   type="text"
                   value={tenant['Last Name'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Last Name': e.target.value})}
                   className="form-input"
                 />
               </div>
               <div className="form-field">
                 <label>Email</label>
                 <input
                   type="email"
                   value={tenant['Email'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Email': e.target.value})}
                   className="form-input"
                 />
               </div>
               <div className="form-field">
                 <label>Phone</label>
                 <input
                   type="tel"
                   value={tenant['Phone'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Phone': e.target.value})}
                   className="form-input"
                 />
               </div>
               <div className="form-field">
                 <label>Gender</label>
                 <select
                   value={tenant['Gender'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Gender': e.target.value})}
                   className="form-input"
                 >
                   <option value="">Select Gender</option>
                   <option value="Male">Male</option>
                   <option value="Female">Female</option>
                   <option value="Other">Other</option>
                 </select>
               </div>
               <div className="form-field">
                 <label>Social Security</label>
                 <input
                   type="text"
                   value={tenant['Social Security'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Social Security': e.target.value})}
                   className="form-input"
                 />
               </div>
               <div className="form-field">
                 <label>Property</label>
                 <select
                   value={tenant.property_id || ''}
                   onChange={(e) => handlePropertyChange(e.target.value)}
                   className="form-input"
                 >
                   <option value="">Select Property</option>
                   {properties.map(prop => (
                     <option key={prop.id} value={prop.id}>
                       {prop['Property Name'] || prop.property_name || `Property ${prop.id}`}
                     </option>
                   ))}
                 </select>
               </div>
               <div className="form-field">
                 <label>Unit</label>
                 <select
                   value={tenant.unit_id || ''}
                   onChange={(e) => handleUnitChange(e.target.value)}
                   className="form-input"
                   disabled={!tenant.property_id}
                 >
                   <option value="">Select Unit</option>
                   {units.map(unit => (
                     <option key={unit.id} value={unit.id}>
                       {unit.unit_number} ({unit.status})
                     </option>
                   ))}
                 </select>
               </div>
               <div className="form-field">
                 <label>Unit Address (Auto-filled) *</label>
                 <input
                   type="text"
                   value={tenant['Unit Address'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Unit Address': e.target.value})}
                   className="form-input"
                   placeholder="Select a unit to auto-fill"
                 />
               </div>
             </div>
           </div>

                     {/* Maintenance Requests */}
          <TenantMaintenance tenantId={tenantId} />

          {/* Tenant Status */}
          <div className="info-section">
            <h2>Tenant Status</h2>
            <div className="form-grid">
                               <div className="form-field">
                  <label>Tenant Code (Auto-generated)</label>
                  <input
                    type="text"
                    value={tenant['Tenant Code'] || ''}
                    className="form-input"
                    placeholder="Auto-generated"
                    readOnly
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Format: T + Property ID (3 digits) + Sequential (4 digits)
                  </small>
                </div>
               <div className="form-field">
                 <label>Lease Status</label>
                 <select
                   value={tenant['Lease Status'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Lease Status': e.target.value})}
                   className="form-input"
                 >
                   <option value="">Select Lease Status</option>
                   <option value="Leaseholder">Leaseholder</option>
                   <option value="Roommate">Roommate</option>
                 </select>
               </div>
               <div className="form-field">
                 <label>Resident Status</label>
                 <select
                   value={tenant['Resident Status'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Resident Status': e.target.value})}
                   className="form-input"
                 >
                   <option value="">Select Resident Status</option>
                   <option value="Current">Current</option>
                   <option value="Past">Past</option>
                   <option value="Future">Future</option>
                   <option value="Prospective">Prospective</option>
                 </select>
               </div>
               <div className="form-field">
                 <label>Move-In Date</label>
                 <input
                   type="date"
                   value={tenant['Move-In Date'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Move-In Date': e.target.value})}
                   className="form-input"
                 />
               </div>
               <div className="form-field">
                 <label>Move-Out Date</label>
                 <input
                   type="date"
                   value={tenant['Move-Out Date'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Move-Out Date': e.target.value})}
                   className="form-input"
                 />
               </div>
               <div className="form-field">
                 <label>Credit Score</label>
                 <input
                   type="number"
                   value={tenant['Credit Score'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Credit Score': e.target.value})}
                   className="form-input"
                   min="300"
                   max="850"
                   placeholder="300-850"
                 />
               </div>
             </div>
           </div>

           {/* Emergency Contact */}
           <div className="info-section">
             <h2>Emergency Contact</h2>
             <div className="form-grid">
               <div className="form-field">
                 <label>Emergency Contact Name</label>
                 <input
                   type="text"
                   value={tenant['Emergency Contact Name'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Emergency Contact Name': e.target.value})}
                   className="form-input"
                 />
               </div>
               <div className="form-field">
                 <label>Emergency Contact Phone</label>
                 <input
                   type="tel"
                   value={tenant['Emergency Contact Phone'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Emergency Contact Phone': e.target.value})}
                   className="form-input"
                 />
               </div>
               <div className="form-field">
                 <label>Relationship</label>
                 <input
                   type="text"
                   value={tenant['Emergency Contact Relationship'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Emergency Contact Relationship': e.target.value})}
                   className="form-input"
                   placeholder="e.g., Spouse, Parent, Friend"
                 />
               </div>
             </div>
           </div>

           {/* Financial Background */}
           <div className="info-section">
             <h2>Financial Background</h2>
             <div className="form-grid">
               <div className="form-field">
                 <label>Employment Status</label>
                 <select
                   value={tenant['Employment Status'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Employment Status': e.target.value})}
                   className="form-input"
                 >
                   <option value="">Select Employment Status</option>
                   <option value="Employed">Employed</option>
                   <option value="Self-Employed">Self-Employed</option>
                   <option value="Unemployed">Unemployed</option>
                   <option value="Student">Student</option>
                   <option value="Retired">Retired</option>
                 </select>
               </div>
               <div className="form-field">
                 <label>Employer Name</label>
                 <input
                   type="text"
                   value={tenant['Employer Name'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Employer Name': e.target.value})}
                   className="form-input"
                 />
               </div>
               <div className="form-field">
                 <label>Monthly Income</label>
                 <input
                   type="number"
                   value={tenant['Monthly Income'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Monthly Income': e.target.value})}
                   className="form-input"
                   placeholder="$0.00"
                 />
               </div>
               <div className="form-field">
                 <label>Rent Amount</label>
                 <input
                   type="number"
                   value={tenant['Rent Amount'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Rent Amount': e.target.value})}
                   className="form-input"
                   placeholder="$0.00"
                 />
               </div>
               <div className="form-field">
                 <label>Security Deposit</label>
                 <input
                   type="number"
                   value={tenant['Security Deposit'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Security Deposit': e.target.value})}
                   className="form-input"
                   placeholder="$0.00"
                 />
               </div>
               <div className="form-field">
                 <label>Insurance Provider</label>
                 <input
                   type="text"
                   value={tenant['Insurance Provider'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Insurance Provider': e.target.value})}
                   className="form-input"
                 />
               </div>
             </div>
           </div>

           {/* Payment Information */}
           <div className="info-section">
             <h2>Payment Information</h2>
             
             {/* Payment Method Selection */}
             <div className="payment-method-section">
               <h3>💳 Payment Method</h3>
               <div className="form-grid">
                 <div className="form-field">
                   <label>Preferred Payment Method</label>
                   <select
                     value={tenant['Payment Method'] || ''}
                     onChange={(e) => setTenant({...tenant, 'Payment Method': e.target.value})}
                     className="form-input"
                   >
                     <option value="">Select Payment Method</option>
                     <option value="Credit">Credit</option>
                     <option value="Check">Check</option>
                     <option value="Bank Transfer">Bank Transfer</option>
                     <option value="Cash">Cash</option>
                   </select>
                 </div>
               </div>
             </div>
             
             {/* Bank Account Setup - Show when payment method is Bank Transfer */}
             {tenant['Payment Method'] === 'Bank Transfer' && (
               <div className="payment-method-section">
                 <h3>🏦 Bank Account Information</h3>
                 <div className="form-grid">
                   <div className="form-field">
                     <label>Bank Name</label>
                     <input
                       type="text"
                       value={tenant['Autopay Bank Name'] || ''}
                       onChange={(e) => setTenant({...tenant, 'Autopay Bank Name': e.target.value})}
                       className="form-input"
                       placeholder="e.g., Chase Bank"
                     />
                   </div>
                   <div className="form-field">
                     <label>Account Type</label>
                     <select
                       value={tenant['Autopay Account Type'] || ''}
                       onChange={(e) => setTenant({...tenant, 'Autopay Account Type': e.target.value})}
                       className="form-input"
                     >
                       <option value="">Select Account Type</option>
                       <option value="checking">Checking</option>
                       <option value="savings">Savings</option>
                     </select>
                   </div>
                   <div className="form-field">
                     <label>Account Number (Last 4 digits)</label>
                     <input
                       type="text"
                       value={tenant['Autopay Account Number'] || ''}
                       onChange={(e) => setTenant({...tenant, 'Autopay Account Number': e.target.value})}
                       className="form-input"
                       placeholder="1234"
                       maxLength="4"
                     />
                   </div>
                   <div className="form-field">
                     <label>Routing Number</label>
                     <input
                       type="text"
                       value={tenant['Autopay Routing Number'] || ''}
                       onChange={(e) => setTenant({...tenant, 'Autopay Routing Number': e.target.value})}
                       className="form-input"
                       placeholder="123456789"
                       maxLength="9"
                     />
                   </div>
                   <div className="form-field">
                     <label>Account Holder Name</label>
                     <input
                       type="text"
                       value={tenant['Autopay Account Holder Name'] || ''}
                       onChange={(e) => setTenant({...tenant, 'Autopay Account Holder Name': e.target.value})}
                       className="form-input"
                       placeholder="John Doe"
                     />
                   </div>
                   <div className="form-field">
                     <label>Bank Account Status</label>
                     <div className="status-indicator">
                       <span className={`status-badge ${tenant['Autopay Is Active'] ? 'active' : 'inactive'}`}>
                         {tenant['Autopay Is Active'] ? '✅ Active' : '❌ Inactive'}
                       </span>
                     </div>
                   </div>
                   
                   {/* Autopay Configuration for Bank */}
                   <div className="form-field">
                     <label>Enable Autopay for Bank Account</label>
                     <div className="toggle-switch">
                       <input
                         type="checkbox"
                         id="autopay-enabled-bank"
                         checked={tenant['Autopay Enabled'] && tenant['Autopay Method'] === 'bank'}
                         onChange={(e) => {
                           if (e.target.checked) {
                             setTenant({
                               ...tenant, 
                               'Autopay Enabled': true,
                               'Autopay Method': 'bank'
                             });
                           } else {
                             setTenant({
                               ...tenant, 
                               'Autopay Enabled': false,
                               'Autopay Method': ''
                             });
                           }
                         }}
                       />
                       <label htmlFor="autopay-enabled-bank" className="toggle-label">
                         <span className="toggle-slider"></span>
                       </label>
                     </div>
                   </div>
                   
                   <div className="form-field" style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                     <button
                       type="button"
                       onClick={() => setShowStripeAutopayModal(true)}
                       className="stripe-autopay-btn"
                       style={{
                         backgroundColor: '#635BFF',
                         color: 'white',
                         border: 'none',
                         padding: '10px 20px',
                         borderRadius: '6px',
                         cursor: 'pointer',
                         fontSize: '14px',
                         fontWeight: '600',
                         flex: 1
                       }}
                     >
                       🔒 Setup Stripe Autopay
                     </button>
                     {tenant['Autopay Enabled'] && tenant['Autopay Method'] === 'bank' && (
                       <button
                         type="button"
                         onClick={handleDeactivateAutopay}
                         className="deactivate-autopay-btn"
                         style={{
                           backgroundColor: '#dc3545',
                           color: 'white',
                           border: 'none',
                           padding: '10px 16px',
                           borderRadius: '6px',
                           cursor: 'pointer',
                           fontSize: '14px'
                         }}
                       >
                         🚫 Deactivate
                       </button>
                     )}
                   </div>
                 </div>
               </div>
             )}

             {/* Credit Card Setup - Show when payment method is Credit */}
             {tenant['Payment Method'] === 'Credit' && (
               <div className="payment-method-section">
                 <h3>💳 Credit Card Information</h3>
                 <div className="form-grid">
                   <div className="form-field">
                     <label>Card Number (Last 4 digits)</label>
                     <input
                       type="text"
                       value={tenant['Credit Card Number'] || ''}
                       onChange={(e) => setTenant({...tenant, 'Credit Card Number': e.target.value})}
                       className="form-input"
                       placeholder="1234"
                       maxLength="4"
                     />
                   </div>
                   <div className="form-field">
                     <label>Expiry Date</label>
                     <input
                       type="text"
                       value={tenant['Credit Card Expiry'] || ''}
                       onChange={(e) => setTenant({...tenant, 'Credit Card Expiry': e.target.value})}
                       className="form-input"
                       placeholder="MM/YY"
                       maxLength="5"
                     />
                   </div>
                   <div className="form-field">
                     <label>CVV</label>
                     <input
                       type="text"
                       value={tenant['Credit Card CVV'] || ''}
                       onChange={(e) => setTenant({...tenant, 'Credit Card CVV': e.target.value})}
                       className="form-input"
                       placeholder="123"
                       maxLength="4"
                     />
                   </div>
                   <div className="form-field">
                     <label>Cardholder Name</label>
                     <input
                       type="text"
                       value={tenant['Credit Card Holder Name'] || ''}
                       onChange={(e) => setTenant({...tenant, 'Credit Card Holder Name': e.target.value})}
                       className="form-input"
                       placeholder="John Doe"
                     />
                   </div>
                   <div className="form-field">
                     <label>Credit Card Status</label>
                     <div className="status-indicator">
                       <span className={`status-badge ${tenant['Credit Card Is Active'] ? 'active' : 'inactive'}`}>
                         {tenant['Credit Card Is Active'] ? '✅ Active' : '❌ Inactive'}
                       </span>
                     </div>
                   </div>
                   
                   {/* Autopay Configuration for Credit Card */}
                   <div className="form-field">
                     <label>Enable Autopay for Credit Card</label>
                     <div className="toggle-switch">
                       <input
                         type="checkbox"
                         id="autopay-enabled-credit"
                         checked={tenant['Autopay Enabled'] && tenant['Autopay Method'] === 'credit'}
                         onChange={(e) => {
                           if (e.target.checked) {
                             setTenant({
                               ...tenant, 
                               'Autopay Enabled': true,
                               'Autopay Method': 'credit'
                             });
                           } else {
                             setTenant({
                               ...tenant, 
                               'Autopay Enabled': false,
                               'Autopay Method': ''
                             });
                           }
                         }}
                       />
                       <label htmlFor="autopay-enabled-credit" className="toggle-label">
                         <span className="toggle-slider"></span>
                       </label>
                     </div>
                   </div>
                   
                   {tenant['Autopay Enabled'] && tenant['Autopay Method'] === 'credit' && (
                     <div className="form-field">
                       <button
                         type="button"
                         onClick={handleDeactivateCreditCard}
                         className="deactivate-credit-card-btn"
                         style={{
                           backgroundColor: '#dc3545',
                           color: 'white',
                           border: 'none',
                           padding: '8px 16px',
                           borderRadius: '4px',
                           cursor: 'pointer',
                           fontSize: '14px'
                         }}
                       >
                         🚫 Deactivate Autopay
                       </button>
                     </div>
                   )}
                 </div>
               </div>
             )}
           </div>

           {/* Lease Information */}
           <div className="info-section">
             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
               <h2 style={{ margin: 0 }}>Lease Information</h2>
               <button 
                 type="button" 
                 className="btn-secondary" 
                 onClick={handleSyncPayments}
                 style={{ backgroundColor: '#6366f1', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px' }}
               >
                 🔄 Sync Rental Payments
               </button>
             </div>
             <div className="form-grid">
               <div className="form-field">
                 <label>Lease Start Date</label>
                 <input
                   type="date"
                   value={tenant['Lease Start Date'] || ''}
                   onChange={(e) => {
                     const startDate = e.target.value;
                     const leaseType = tenant['Lease Type'] || '';
                     const endDate = calculateLeaseEndDate(startDate, leaseType);
                     setTenant({
                       ...tenant, 
                       'Lease Start Date': startDate,
                       'Lease End Date': endDate
                     });
                   }}
                   className="form-input"
                 />
               </div>
               <div className="form-field">
                 <label>Lease End Date</label>
                 <input
                   type="date"
                   value={tenant['Lease End Date'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Lease End Date': e.target.value})}
                   className="form-input"
                 />
               </div>
               <div className="form-field">
                 <label>Lease Type</label>
                 <select
                   value={tenant['Lease Type'] || ''}
                   onChange={(e) => {
                     const leaseType = e.target.value;
                     const startDate = tenant['Lease Start Date'] || '';
                     const endDate = calculateLeaseEndDate(startDate, leaseType);
                     setTenant({
                       ...tenant, 
                       'Lease Type': leaseType,
                       'Lease End Date': endDate
                     });
                   }}
                   className="form-input"
                 >
                   <option value="">Select Lease Type</option>
                   <option value="6 months">6 months</option>
                   <option value="12 months">12 months</option>
                   <option value="18 months">18 months</option>
                   <option value="24 months">24 months</option>
                 </select>
               </div>
               <div className="form-field">
                 <label>Monthly Rent</label>
                 <input
                   type="number"
                   step="0.01"
                   placeholder="0.00"
                   value={tenant['Monthly Rent'] || ''}
                   onChange={(e) => setTenant({...tenant, 'Monthly Rent': e.target.value})}
                   className="form-input"
                 />
               </div>
             </div>
           </div>

           {/* Upcoming Payments */}
           <div className="info-section">
             <div className="section-header">
               <h2>Upcoming Payments</h2>
               <button 
                 onClick={fetchUpcomingPayments}
                 className="add-attribute-button"
                 disabled={loadingPayments}
               >
                 {loadingPayments ? '🔄' : '🔄 Refresh'}
               </button>
             </div>
             <div className="upcoming-payments-list">
               {loadingPayments ? (
                 <div className="loading-payments">Loading payments...</div>
               ) : upcomingPayments.length === 0 ? (
                 <p className="no-payments">No upcoming payments found.</p>
               ) : (
                 upcomingPayments.map(payment => (
                   <div 
                     key={payment.id} 
                     className="payment-item clickable"
                     onClick={() => {
                       setProcessingPayment(payment);
                       setShowPaymentProcessing(true);
                       setSelectedPaymentMethod('');
                       setCheckFile(null);
                       setCreditCardData({
                         cardNumber: '',
                         expiryDate: '',
                         cvv: '',
                         cardholderName: '',
                         billingAddress: ''
                       });
                     }}
                     style={{ cursor: 'pointer' }}
                   >
                     <div className="payment-info">
                       <div className="payment-amount">
                         {formatCurrency(payment.amount)}
                       </div>
                       <div className="payment-date">
                         Due: {formatDate(payment.due_date)}
                       </div>
                       <div className="payment-status">
                         <span className={`status-badge ${payment.status}`}>
                           {payment.status}
                         </span>
                       </div>
                     </div>
                     <div className="payment-actions">
                       {payment.payment_method && (
                         <div className="payment-method">
                           {payment.payment_method}
                         </div>
                       )}
                       {payment.reference_number && (
                         <div className="payment-reference">
                           Ref: {payment.reference_number}
                         </div>
                       )}
                       <div className="payment-action-hint">
                         Click to process payment
                       </div>
                     </div>
                   </div>
                 ))
               )}
             </div>
           </div>

                       {/* Documents */}
            <div className="info-section">
              <div className="section-header">
                <h2>Documents</h2>
                <button 
                  onClick={() => setShowDocumentUploadModal(true)}
                  className="add-attribute-button"
                >
                  📄 Upload Document
                </button>
              </div>
              <div className="documents-list">
                {documents.length === 0 ? (
                  <p className="no-documents">No documents uploaded yet.</p>
                ) : (
                  documents.map(doc => (
                    <div key={doc.id} className="document-item">
                      <div className="document-info">
                        <div className="document-name">{doc.original_filename}</div>
                        <div className="document-meta">
                          {formatFileSize(doc.file_size)} • {formatDate(doc.upload_date)}
                        </div>
                        {doc.description && (
                          <div className="document-description">{doc.description}</div>
                        )}
                      </div>
                      <div className="document-actions">
                        <button 
                          onClick={() => handleDocumentDownload(doc.id)}
                          className="document-action-btn download"
                        >
                          📥
                        </button>
                        <button 
                          onClick={() => handleDocumentDelete(doc.id)}
                          className="document-action-btn delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Legal Status */}
            <div className="info-section">
              <div className="section-header">
                <h2>Legal Status</h2>
                <button 
                  onClick={openLegalStatusModal}
                  className="add-attribute-button"
                >
                  ⚖️ Update Status
                </button>
              </div>
              <div className="legal-status-display">
                <div className="current-status">
                  <div className="status-badge" style={{ backgroundColor: getLegalStatusColor(legalStatus.status) }}>
                    {getLegalStatusLabel(legalStatus.status)}
                  </div>
                  {legalStatus.status_date && (
                    <div className="status-date">
                      Since: {formatDate(legalStatus.status_date)}
                    </div>
                  )}
                  {legalStatus.notes && (
                    <div className="status-notes">
                      Notes: {legalStatus.notes}
                    </div>
                  )}
                </div>
                <div className="legal-status-actions">
                  <button 
                    onClick={fetchLegalStatusHistory}
                    className="history-button"
                  >
                    📋 View History
                  </button>
                  {legalStatus.id && (
                    <button 
                      onClick={() => openDocumentLinkModal('legal_status', legalStatus.id)}
                      className="complaint-action-btn link"
                      title="Link Documents"
                    >
                      📎 Link Documents
                    </button>
                  )}
                </div>
              </div>
              
              {/* Linked Documents for Current Legal Status */}
              {legalStatus.id && legalStatusDocuments[legalStatus.id] && legalStatusDocuments[legalStatus.id].length > 0 && (
                <div className="linked-documents">
                  <h4>Linked Documents:</h4>
                  <div className="linked-documents-list">
                    {legalStatusDocuments[legalStatus.id].map(doc => (
                      <div key={doc.id} className="linked-document-item">
                        <div className="linked-document-info">
                          <div className="linked-document-name">{doc.original_filename}</div>
                          <div className="linked-document-meta">
                            {formatFileSize(doc.file_size)} • {formatDate(doc.upload_date)}
                          </div>
                        </div>
                        <div className="linked-document-actions">
                          <button 
                            onClick={() => handleDocumentDownload(doc.id)}
                            className="document-action-btn download"
                            title="Download"
                          >
                            📥
                          </button>
                          <button 
                            onClick={() => unlinkDocument(doc.id, 'legal_status', legalStatus.id)}
                            className="document-action-btn unlink"
                            title="Unlink"
                          >
                            🔗
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {legalStatusHistory.length > 0 && (
                <div className="legal-status-history">
                  <h3>Status History</h3>
                  <div className="history-list">
                    {legalStatusHistory.map((status, index) => (
                      <div key={index} className="history-item">
                        <div className="history-status">
                          <div className="status-badge small" style={{ backgroundColor: getLegalStatusColor(status.status) }}>
                            {getLegalStatusLabel(status.status)}
                          </div>
                          <div className="history-date">{formatDate(status.status_date)}</div>
                        </div>
                        {status.notes && (
                          <div className="history-notes">{status.notes}</div>
                        )}
                        <div className="history-actions">
                          <button 
                            onClick={() => openDocumentLinkModal('legal_status', status.id)}
                            className="complaint-action-btn link small"
                            title="Link Documents"
                          >
                            📎
                          </button>
                        </div>
                        
                        {/* Linked Documents for Historical Status */}
                        {legalStatusDocuments[status.id] && legalStatusDocuments[status.id].length > 0 && (
                          <div className="linked-documents history">
                            <h5>Linked Documents:</h5>
                            <div className="linked-documents-list">
                              {legalStatusDocuments[status.id].map(doc => (
                                <div key={doc.id} className="linked-document-item">
                                  <div className="linked-document-info">
                                    <div className="linked-document-name">{doc.original_filename}</div>
                                    <div className="linked-document-meta">
                                      {formatFileSize(doc.file_size)} • {formatDate(doc.upload_date)}
                                    </div>
                                  </div>
                                  <div className="linked-document-actions">
                                    <button 
                                      onClick={() => handleDocumentDownload(doc.id)}
                                      className="document-action-btn download"
                                      title="Download"
                                    >
                                      📥
                                    </button>
                                    <button 
                                      onClick={() => unlinkDocument(doc.id, 'legal_status', status.id)}
                                      className="document-action-btn unlink"
                                      title="Unlink"
                                    >
                                      🔗
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Complaints */}
            <div className="info-section">
              <div className="section-header">
                <h2>Complaints</h2>
                <button 
                  onClick={() => setShowAddComplaintModal(true)}
                  className="add-attribute-button"
                >
                  📝 File Complaint
                </button>
              </div>
              <div className="complaints-list">
                {complaints.length === 0 ? (
                  <p className="no-complaints">No complaints filed yet.</p>
                ) : (
                  complaints.map(complaint => (
                    <div key={complaint.id} className="complaint-item">
                      <div className="complaint-header">
                        <div className="complaint-type">
                          {getComplaintTypeLabel(complaint.complaint_type)}
                        </div>
                        <div className="complaint-severity" style={{ backgroundColor: getSeverityColor(complaint.severity) }}>
                          {complaint.severity.toUpperCase()}
                        </div>
                        <div className="complaint-status" style={{ backgroundColor: getStatusColor(complaint.status) }}>
                          {complaint.status.toUpperCase()}
                        </div>
                      </div>
                      <div className="complaint-content">
                        <div className="complaint-description">{complaint.description}</div>
                        <div className="complaint-meta">
                          Filed: {formatDate(complaint.filed_date)}
                          {complaint.resolved_date && (
                            <span> • Resolved: {formatDate(complaint.resolved_date)}</span>
                          )}
                        </div>
                        {complaint.resolution_notes && (
                          <div className="complaint-resolution">
                            Resolution: {complaint.resolution_notes}
                          </div>
                        )}
                      </div>
                      <div className="complaint-actions">
                        <button 
                          onClick={() => openEditComplaintModal(complaint)}
                          className="complaint-action-btn edit"
                        >
                          ✏️
                        </button>
                        <button 
                          onClick={() => openDocumentLinkModal('complaint', complaint.id)}
                          className="complaint-action-btn link"
                          title="Link Documents"
                        >
                          📎
                        </button>
                        <button 
                          onClick={() => handleDeleteComplaint(complaint.id)}
                          className="complaint-action-btn delete"
                        >
                          🗑️
                        </button>
                      </div>
                      
                      {/* Linked Documents */}
                      {complaintDocuments[complaint.id] && complaintDocuments[complaint.id].length > 0 && (
                        <div className="linked-documents">
                          <h4>Linked Documents:</h4>
                          <div className="linked-documents-list">
                            {complaintDocuments[complaint.id].map(doc => (
                              <div key={doc.id} className="linked-document-item">
                                <div className="linked-document-info">
                                  <div className="linked-document-name">{doc.original_filename}</div>
                                  <div className="linked-document-meta">
                                    {formatFileSize(doc.file_size)} • {formatDate(doc.upload_date)}
                                  </div>
                                </div>
                                <div className="linked-document-actions">
                                  <button 
                                    onClick={() => handleDocumentDownload(doc.id)}
                                    className="document-action-btn download"
                                    title="Download"
                                  >
                                    📥
                                  </button>
                                  <button 
                                    onClick={() => unlinkDocument(doc.id, 'complaint', complaint.id)}
                                    className="document-action-btn unlink"
                                    title="Unlink"
                                  >
                                    🔗
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

           {/* Custom Attributes */}
           <div className="info-section">
             <div className="section-header">
               <h2>Additional Information</h2>
               <div className="attribute-actions">
                 <button 
                   onClick={() => setShowAddAttributeModal(true)}
                   className="add-attribute-button"
                 >
                   + Add Attribute
                 </button>
               </div>
             </div>
             <div className="form-grid">
               {tenantAttributes.slice(27).map(attr => {
                 if (attr === 'Property') return null; // Skip Property as it's handled above
                 
                 const value = tenant[attr] || '';
                 const isCurrency = attributeTypes[attr] === 'currency';
                 const isNumber = attributeTypes[attr] === 'number';
                 
                 return (
                   <div key={attr} className="form-field">
                     <label>{attr}</label>
                     <input
                       type={isNumber ? 'number' : 'text'}
                       value={isCurrency ? formatCurrency(value) : value}
                       onChange={(e) => setTenant({...tenant, [attr]: e.target.value})}
                       className="form-input"
                       placeholder={`Enter ${attr.toLowerCase()}`}
                     />
                   </div>
                 );
               })}
             </div>
           </div>
         </div>
       </div>

             {/* Add Attribute Modal */}
       {showAddAttributeModal && (
         <div className="modal-overlay">
           <div className="modal-content">
             <h2>Add New Attribute</h2>
             <div className="form-field">
               <label>Attribute Name</label>
               <input
                 type="text"
                 value={newAttribute}
                 onChange={(e) => setNewAttribute(e.target.value)}
                 className="form-input"
                 placeholder="e.g., Monthly Rent, Gender, Move In Date"
               />
             </div>
             <div className="form-field">
               <label>Data Type</label>
               <select
                 value={newAttributeType}
                 onChange={(e) => setNewAttributeType(e.target.value)}
                 className="form-input"
               >
                 <option value="string">Text</option>
                 <option value="number">Number</option>
                 <option value="currency">Currency</option>
               </select>
             </div>
             <div className="modal-actions">
               <button 
                 onClick={handleAddAttribute}
                 className="save-button"
                 disabled={saving || !newAttribute.trim()}
               >
                 {saving ? 'Adding...' : 'Add Attribute'}
               </button>
               <button 
                 onClick={() => setShowAddAttributeModal(false)}
                 className="cancel-button"
               >
                 Cancel
               </button>
             </div>
           </div>
         </div>
       )}

               {/* Document Upload Modal */}
        {showDocumentUploadModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>Upload Document</h2>
              <form onSubmit={handleDocumentUpload}>
                <div className="form-field">
                  <label>Select File</label>
                  <input
                    type="file"
                    name="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Description (Optional)</label>
                  <input
                    type="text"
                    name="description"
                    className="form-input"
                    placeholder="e.g., Lease Agreement, ID Document, etc."
                  />
                </div>
                <div className="modal-actions">
                  <button 
                    type="submit"
                    className="save-button"
                    disabled={uploadingDocument}
                  >
                    {uploadingDocument ? 'Uploading...' : 'Upload Document'}
                  </button>
                  <button 
                    type="button"
                    onClick={() => setShowDocumentUploadModal(false)}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Notes - inside content-grid span full width */}
        <div className="tenant-content">
          <div className="info-section">
          <h2>Notes</h2>
          <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="form-field">
              <label>Add Note</label>
              <textarea
                value={tenantNewNote}
                onChange={(e) => setTenantNewNote(e.target.value)}
                className="form-input"
                rows={3}
                placeholder="Type a note and click Add. Notes are immutable and timestamped."
                style={{ resize: 'vertical' }}
              />
              <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-end' }}>
                <button className="add-attribute-button" onClick={handleAddTenantNote} disabled={!tenantNewNote.trim() || saving}>
                  + Add Note
                </button>
              </div>
            </div>
          </div>
          <div style={{ marginTop: '12px', maxHeight: '320px', overflowY: 'auto', paddingRight: '4px' }}>
            {tenantNotes.length === 0 ? (
              <div style={{ color: '#6c757d' }}>No notes yet.</div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {tenantNotes.map(n => (
                  <li key={n.id} style={{ background: '#ffffff', border: '1px solid #e9ecef', borderRadius: '10px', padding: '12px', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <div style={{ fontSize: '12px', color: '#6c757d' }}>{new Date(n.created_at).toLocaleString()}</div>
                      {n.created_by && <div style={{ fontSize: '12px', color: '#6c757d' }}>{n.created_by}</div>}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{n.note_text}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        </div>

        {showLegalStatusModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>Update Legal Status</h2>
              <div className="form-field">
                <label>Legal Status</label>
                <select
                  value={newLegalStatus.status}
                  onChange={(e) => setNewLegalStatus({...newLegalStatus, status: e.target.value})}
                  className="form-input"
                >
                  <option value="good_standing">Good Standing</option>
                  <option value="light_offender">Light Offender</option>
                  <option value="heavy_offender">Heavy Offender</option>
                  <option value="on_notice">On Notice</option>
                  <option value="eviction">Eviction</option>
                </select>
              </div>
              <div className="form-field">
                <label>Notes (Optional)</label>
                <textarea
                  value={newLegalStatus.notes}
                  onChange={(e) => setNewLegalStatus({...newLegalStatus, notes: e.target.value})}
                  className="form-input"
                  placeholder="Add notes about this status change..."
                  rows="3"
                />
              </div>
              <div className="modal-actions">
                <button 
                  onClick={handleUpdateLegalStatus}
                  className="save-button"
                  disabled={saving}
                >
                  {saving ? 'Updating...' : 'Update Status'}
                </button>
                <button 
                  onClick={() => setShowLegalStatusModal(false)}
                  className="cancel-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Complaint Modal */}
        {showAddComplaintModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>File New Complaint</h2>
              <div className="form-field">
                <label>Complaint Type</label>
                <select
                  value={newComplaint.complaint_type}
                  onChange={(e) => setNewComplaint({...newComplaint, complaint_type: e.target.value})}
                  className="form-input"
                >
                  <option value="lease_violation">Lease Violation</option>
                  <option value="rent_late">Rent Late</option>
                  <option value="utility_late">Utility Late</option>
                  <option value="noise_complaint">Noise Complaint</option>
                  <option value="property_damage">Property Damage</option>
                  <option value="unauthorized_pet">Unauthorized Pet</option>
                  <option value="unauthorized_occupant">Unauthorized Occupant</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-field">
                <label>Severity</label>
                <select
                  value={newComplaint.severity}
                  onChange={(e) => setNewComplaint({...newComplaint, severity: e.target.value})}
                  className="form-input"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div className="form-field">
                <label>Description</label>
                <textarea
                  value={newComplaint.description}
                  onChange={(e) => setNewComplaint({...newComplaint, description: e.target.value})}
                  className="form-input"
                  placeholder="Describe the complaint in detail..."
                  rows="4"
                  required
                />
              </div>
              
              {/* Document Linking Section */}
              <div className="form-field">
                <label>Linked Documents</label>
                <div className="document-linking-section">
                  <button
                    type="button"
                    onClick={openNewComplaintDocumentModal}
                    className="link-document-btn"
                  >
                    📎 Link Documents
                  </button>
                  {newComplaintDocuments.length > 0 && (
                    <div className="linked-documents">
                      {newComplaintDocuments.map((doc) => (
                        <div key={doc.id} className="linked-document-item">
                          <span>{doc.filename}</span>
                          <button
                            onClick={() => unlinkDocumentFromNewComplaint(doc.id)}
                            className="unlink-btn"
                            title="Unlink document"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="modal-actions">
                <button 
                  onClick={handleAddComplaint}
                  className="save-button"
                  disabled={saving || !newComplaint.description.trim()}
                >
                  {saving ? 'Filing...' : 'File Complaint'}
                </button>
                <button 
                  onClick={() => {
                    setShowAddComplaintModal(false);
                    setNewComplaintDocuments([]); // Reset linked documents
                  }}
                  className="cancel-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Complaint Modal */}
        {editingComplaint && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>Edit Complaint</h2>
              <div className="form-field">
                <label>Complaint Type</label>
                <select
                  value={editingComplaint.complaint_type}
                  onChange={(e) => setEditingComplaint({...editingComplaint, complaint_type: e.target.value})}
                  className="form-input"
                >
                  <option value="lease_violation">Lease Violation</option>
                  <option value="rent_late">Rent Late</option>
                  <option value="utility_late">Utility Late</option>
                  <option value="noise_complaint">Noise Complaint</option>
                  <option value="property_damage">Property Damage</option>
                  <option value="unauthorized_pet">Unauthorized Pet</option>
                  <option value="unauthorized_occupant">Unauthorized Occupant</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-field">
                <label>Severity</label>
                <select
                  value={editingComplaint.severity}
                  onChange={(e) => setEditingComplaint({...editingComplaint, severity: e.target.value})}
                  className="form-input"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div className="form-field">
                <label>Status</label>
                <select
                  value={editingComplaint.status}
                  onChange={(e) => setEditingComplaint({...editingComplaint, status: e.target.value})}
                  className="form-input"
                >
                  <option value="open">Open</option>
                  <option value="investigating">Investigating</option>
                  <option value="resolved">Resolved</option>
                  <option value="dismissed">Dismissed</option>
                </select>
              </div>
              <div className="form-field">
                <label>Description</label>
                <textarea
                  value={editingComplaint.description}
                  onChange={(e) => setEditingComplaint({...editingComplaint, description: e.target.value})}
                  className="form-input"
                  placeholder="Describe the complaint in detail..."
                  rows="4"
                  required
                />
              </div>
              <div className="form-field">
                <label>Resolution Notes (Optional)</label>
                <textarea
                  value={editingComplaint.resolution_notes || ''}
                  onChange={(e) => setEditingComplaint({...editingComplaint, resolution_notes: e.target.value})}
                  className="form-input"
                  placeholder="Add resolution notes..."
                  rows="3"
                />
              </div>
              <div className="modal-actions">
                <button 
                  onClick={handleUpdateComplaint}
                  className="save-button"
                  disabled={saving || !editingComplaint.description.trim()}
                >
                  {saving ? 'Updating...' : 'Update Complaint'}
                </button>
                <button 
                  onClick={() => setEditingComplaint(null)}
                  className="cancel-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
                 )}

        {/* Document Link Modal */}
        {showDocumentLinkModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>Link Documents</h2>
              <p>Select a document to link to this {linkingTo?.type === 'complaint' ? 'complaint' : 'legal status change'}:</p>
              <div className="available-documents">
                {documents.length === 0 ? (
                  <p className="no-documents">No documents available. Please upload documents first.</p>
                ) : (
                  documents.map(doc => (
                    <div key={doc.id} className="document-link-item">
                      <div className="document-link-info">
                        <div className="document-link-name">{doc.original_filename}</div>
                        <div className="document-link-meta">
                          {formatFileSize(doc.file_size)} • {formatDate(doc.upload_date)}
                        </div>
                        {doc.description && (
                          <div className="document-link-description">{doc.description}</div>
                        )}
                      </div>
                      <button 
                        onClick={() => linkDocument(doc.id)}
                        className="link-document-btn"
                      >
                        Link Document
                      </button>
                    </div>
                  ))
                )}
              </div>
              <div className="modal-actions">
                <button 
                  onClick={() => setShowDocumentLinkModal(false)}
                  className="cancel-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* New Complaint Document Link Modal */}
        {showNewComplaintDocumentModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>Link Documents to New Complaint</h2>
              <p>Select documents to link to this complaint:</p>
              <div className="available-documents">
                {documents.length === 0 ? (
                  <p className="no-documents">No documents available. Please upload documents first.</p>
                ) : (
                  documents.map(doc => (
                    <div key={doc.id} className="document-link-item">
                      <div className="document-link-info">
                        <div className="document-link-name">{doc.original_filename}</div>
                        <div className="document-link-meta">
                          {formatFileSize(doc.file_size)} • {formatDate(doc.upload_date)}
                        </div>
                        {doc.description && (
                          <div className="document-link-description">{doc.description}</div>
                        )}
                      </div>
                      <button 
                        onClick={() => linkDocumentToNewComplaint(doc.id)}
                        className="link-document-btn"
                        disabled={newComplaintDocuments.find(d => d.id === doc.id)}
                      >
                        {newComplaintDocuments.find(d => d.id === doc.id) ? 'Linked' : 'Link Document'}
                      </button>
                    </div>
                  ))
                )}
              </div>
              <div className="modal-actions">
                <button 
                  onClick={() => setShowNewComplaintDocumentModal(false)}
                  className="cancel-button"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Processing Modal */}
        {showPaymentProcessing && processingPayment && (
          <div className="modal-overlay">
            <div className="modal payment-processing-modal">
              <div className="modal-header">
                <h3>Process Payment - {formatCurrency(processingPayment.amount)}</h3>
                <button className="close-btn" onClick={() => setShowPaymentProcessing(false)}>×</button>
              </div>
              
              <div className="payment-processing-content">
                
                <div className="payment-method-selection">
                  <h4>Select Payment Method</h4>
                  <div className="payment-methods">
                    <div className="payment-method-option">
                      <input
                        type="radio"
                        id="credit-card"
                        name="payment-method"
                        value="Credit Card"
                        onChange={(e) => {
                          setSelectedPaymentMethod(e.target.value);
                        }}
                      />
                      <label htmlFor="credit-card">
                        <span className="icon">💳</span>
                        Credit Card
                      </label>
                    </div>
                    
                    <div className="payment-method-option">
                      <input
                        type="radio"
                        id="check"
                        name="payment-method"
                        value="Check"
                        onChange={(e) => {
                          setSelectedPaymentMethod(e.target.value);
                        }}
                      />
                      <label htmlFor="check">
                        <span className="icon">📄</span>
                        Check
                      </label>
                    </div>
                    
                    <div className="payment-method-option">
                      <input
                        type="radio"
                        id="cash"
                        name="payment-method"
                        value="Cash"
                        onChange={(e) => {
                          setSelectedPaymentMethod(e.target.value);
                        }}
                      />
                      <label htmlFor="cash">
                        <span className="icon">💰</span>
                        Cash
                      </label>
                    </div>
                    
                    <div className="payment-method-option">
                      <input
                        type="radio"
                        id="bank-transfer"
                        name="payment-method"
                        value="Bank Transfer"
                        onChange={(e) => {
                          setSelectedPaymentMethod(e.target.value);
                        }}
                      />
                      <label htmlFor="bank-transfer">
                        <span className="icon">🏦</span>
                        Bank Transfer
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* Credit Card Form */}
                <div className="credit-card-form" style={{display: selectedPaymentMethod === 'Credit Card' ? 'block' : 'none'}}>
                  <h4>Credit Card Information</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Card Number</label>
                      <input
                        type="text"
                        placeholder="1234 5678 9012 3456"
                        value={creditCardData.cardNumber}
                        onChange={(e) => setCreditCardData({...creditCardData, cardNumber: e.target.value})}
                        maxLength="19"
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Cardholder Name</label>
                      <input
                        type="text"
                        placeholder="John Doe"
                        value={creditCardData.cardholderName}
                        onChange={(e) => setCreditCardData({...creditCardData, cardholderName: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Expiry Date</label>
                      <input
                        type="text"
                        placeholder="MM/YY"
                        value={creditCardData.expiryDate}
                        onChange={(e) => setCreditCardData({...creditCardData, expiryDate: e.target.value})}
                        maxLength="5"
                      />
                    </div>
                    <div className="form-group">
                      <label>CVV</label>
                      <input
                        type="text"
                        placeholder="123"
                        value={creditCardData.cvv}
                        onChange={(e) => setCreditCardData({...creditCardData, cvv: e.target.value})}
                        maxLength="4"
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Billing Address</label>
                      <input
                        type="text"
                        placeholder="123 Main St, City, State 12345"
                        value={creditCardData.billingAddress}
                        onChange={(e) => setCreditCardData({...creditCardData, billingAddress: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Check Upload Form */}
                <div className="check-upload-form" style={{display: selectedPaymentMethod === 'Check' ? 'block' : 'none'}}>
                  <h4>Upload Check Document</h4>
                  <p className="upload-instructions">
                    Please upload a scanned image or PDF of the check. 
                    Supported formats: JPEG, PNG, GIF, PDF (Max 10MB)
                  </p>
                  
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="check-file"
                      accept=".jpg,.jpeg,.png,.gif,.pdf"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="check-file" className="file-upload-label">
                      <span className="icon">📁</span>
                      {checkFile ? checkFile.name : 'Choose File'}
                    </label>
                  </div>
                  
                  {checkFile && (
                    <div className="file-info">
                      <p>Selected file: {checkFile.name}</p>
                      <p>Size: {(checkFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  )}
                </div>
                
                {/* Processing Status */}
                {(processingPayment || checkUploading) && (
                  <div className="processing-status">
                    <div className="spinner"></div>
                    <p>
                      {checkUploading ? 'Uploading check document...' : 'Processing payment...'}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="modal-actions">
                <button 
                  className="btn-secondary" 
                  onClick={() => {
                    setShowPaymentProcessing(false);
                    setSelectedPaymentMethod('');
                    setCheckFile(null);
                    setCreditCardData({
                      cardNumber: '',
                      expiryDate: '',
                      cvv: '',
                      cardholderName: '',
                      billingAddress: ''
                    });
                  }}
                  disabled={processingPayment || checkUploading}
                >
                  Cancel
                </button>
                <button 
                  className="btn-primary"
                  onClick={() => handleProcessPayment(processingPayment.id, selectedPaymentMethod)}
                  disabled={!selectedPaymentMethod || processingPayment || checkUploading}
                >
                  {processingPayment ? 'Processing...' : 'Process Payment'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stripe Autopay Setup Modal */}
        {showStripeAutopayModal && tenant && (
          <StripeAutopaySetup
            tenantId={tenant.id}
            tenantName={`${tenant['First Name'] || tenant.first_name || ''} ${tenant['Last Name'] || tenant.last_name || ''}`.trim()}
            tenantEmail={tenant['Email'] || tenant.email}
            onSuccess={(data) => {
              setShowStripeAutopayModal(false);
              if (data.deactivated) {
                setSuccess('Autopay deactivated successfully!');
                setTenant({
                  ...tenant,
                  'Autopay Enabled': 0,
                  'Autopay Is Active': 0
                });
              } else {
                setSuccess('Autopay setup successfully!');
                setTenant({
                  ...tenant,
                  'Autopay Enabled': 1,
                  'Autopay Is Active': 1,
                  'Autopay Method': data.autopay_method || tenant['Autopay Method']
                });
              }
              setTimeout(() => setSuccess(''), 3000);
              fetchTenantData();
            }}
            onClose={() => setShowStripeAutopayModal(false)}
            mode="manager"
          />
        )}
     </div>
   );
 }

export default TenantEditPage; 